import React from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Terminal, CheckCircle2, Zap } from 'lucide-react';

/**
 * Paste-cURL importer dialog.
 */
export function CurlImportDialog({
    open,
    onOpenChange,
    platform,
    text,
    onTextChange,
    inAppId,
    onInAppIdChange,
    amount,
    onAmountChange,
    importing,
    result,
    onSubmit,
}) {
    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-3xl">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <Terminal className="h-5 w-5 text-emerald-700" /> Import from cURL
                        {platform && (
                            <span className="text-muted-foreground font-normal">\u00b7 {platform}</span>
                        )}
                    </DialogTitle>
                    <DialogDescription>
                        Paste a "Copy as cURL" command from DevTools to auto-configure the bridge.
                    </DialogDescription>
                </DialogHeader>
                {!result ? (
                    <div className="space-y-3">
                        <Textarea
                            rows={10}
                            value={text}
                            onChange={(e) => onTextChange(e.target.value)}
                            placeholder="curl 'https://...' -H '...' --data-raw '...'"
                            className="font-mono text-[11px]"
                            data-testid="admin-curl-input"
                        />
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <Label className="text-xs">In-app ID</Label>
                                <Input
                                    value={inAppId}
                                    onChange={(e) => onInAppIdChange(e.target.value)}
                                    className="font-mono text-xs mt-1"
                                    data-testid="admin-curl-uid"
                                />
                            </div>
                            <div>
                                <Label className="text-xs">Amount</Label>
                                <Input
                                    value={amount}
                                    onChange={(e) => onAmountChange(e.target.value)}
                                    className="font-mono text-xs mt-1"
                                    data-testid="admin-curl-amount"
                                />
                            </div>
                        </div>
                        <div className="flex justify-end gap-2">
                            <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
                            <Button
                                onClick={onSubmit}
                                disabled={importing || !text.trim()}
                                className="bg-emerald-700 hover:bg-emerald-800"
                                data-testid="admin-curl-import-button"
                            >
                                <Zap className="h-4 w-4 mr-1" /> {importing ? 'Importing\u2026' : 'Import'}
                            </Button>
                        </div>
                    </div>
                ) : (
                    <div className="space-y-3">
                        <div className="rounded-xl bg-emerald-50/80 border border-emerald-200 p-3 flex items-start gap-2">
                            <CheckCircle2 className="h-5 w-5 text-emerald-700 mt-0.5" />
                            <div className="font-semibold text-emerald-900">cURL imported successfully</div>
                        </div>
                        <pre className="font-mono text-[11px] bg-secondary/40 rounded-lg p-3 max-h-44 overflow-auto whitespace-pre-wrap break-all">
                            {result.preview?.payload_template || '(empty)'}
                        </pre>
                        <div className="flex justify-end">
                            <Button onClick={() => onOpenChange(false)} className="bg-[#0E2A47] hover:bg-[#0B223A]">
                                Done
                            </Button>
                        </div>
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
}

export default CurlImportDialog;
